import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useApiToken } from '../../auth/AuthProvider';
import {
  uploadReceipt, listReceiptsByDateRange, listReceipts, deleteReceipt, replaceReceiptFile,
  ReceiptMetadata,
} from '../../services/api';
import { deriveCategory, getCategoryStyle } from '../../services/categoryUtils';
import ReceiptPreviewModal from '../../components/ReceiptPreviewModal';
import LoadingAnimation from '../../components/LoadingAnimation';

interface Props {
  startDate: string;
  endDate: string;
  receipts: ReceiptMetadata[];
  preSelectedIds?: string[];
  onReceiptsChange: (receipts: ReceiptMetadata[]) => void;
  onNext: (selectedReceipts: ReceiptMetadata[]) => void;
  onBack: () => void;
  // Override the Next/Back button labels (e.g. "Done"/"Cancel" when used inline).
  nextLabel?: string;
  backLabel?: string;
}

const styles: Record<string, React.CSSProperties> = {
  dropZone: { border: '2px dashed #cbd5e0', borderRadius: 8, padding: 24, textAlign: 'center', cursor: 'pointer', marginBottom: 16, color: '#666', fontSize: 14 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#1a365d', margin: '16px 0 8px' },
  toolbar: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' as const },
  selectAllBtn: { background: '#edf2f7', color: '#333', border: '1px solid #cbd5e0', padding: '5px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  receiptGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 },
  receiptCard: { border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: 'white', position: 'relative' as const, cursor: 'pointer' },
  receiptCardSelected: { border: '2px solid #0078d4', borderRadius: 8, overflow: 'hidden', background: '#ebf5ff', position: 'relative' as const, cursor: 'pointer' },
  checkbox: { position: 'absolute' as const, top: 28, left: 6, width: 16, height: 16, borderRadius: 3, background: 'white', border: '2px solid #a0aec0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, cursor: 'pointer', zIndex: 2 },
  checkboxSelected: { position: 'absolute' as const, top: 28, left: 6, width: 16, height: 16, borderRadius: 3, background: '#0078d4', border: '2px solid #0078d4', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, cursor: 'pointer', zIndex: 2 },
  previewBtn: { position: 'absolute' as const, top: 4, right: 4, background: 'rgba(255,255,255,0.92)', border: '1px solid #cbd5e0', padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 600, cursor: 'pointer', zIndex: 2, color: '#0078d4' },
  previewImg: { width: '100%', height: 105, objectFit: 'cover' as const, background: '#f7fafc' },
  imageFallback: { width: '100%', height: 105, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  cardInfo: { padding: '6px 8px' },
  cardDate: { fontSize: 11, fontWeight: 600, color: '#1a365d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  cardDetail: { fontSize: 10, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  cardAmount: { fontSize: 12, fontWeight: 700, color: '#38a169', marginTop: 1 },
  deleteBtn: { background: 'none', border: 'none', color: '#e53e3e', cursor: 'pointer', fontSize: 10 },
  btn: { background: '#0078d4', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 6, fontSize: 14, cursor: 'pointer', fontWeight: 600 },
  btnOutline: { background: 'white', color: '#333', border: '1px solid #cbd5e0', padding: '10px 20px', borderRadius: 6, fontSize: 14, cursor: 'pointer' },
  actions: { display: 'flex', gap: 12, marginTop: 24 },
  selectedReceiptsSection: { marginTop: 16, padding: 12, background: '#f7fafc', borderRadius: 8, border: '1px solid #e2e8f0' },
  hint: { fontSize: 12, color: '#888', marginBottom: 8 },
  emptyState: { color: '#999', fontStyle: 'italic', fontSize: 13, padding: '12px 0' },
  dupWarning: { background: '#fff5f5', border: '1px solid #fc8181', borderRadius: 8, padding: '10px 14px', marginBottom: 14 },
  dupTitle: { fontSize: 13, fontWeight: 700, color: '#9b2c2c', marginBottom: 4 },
  dupItem: { fontSize: 12, color: '#742a2a', marginBottom: 2, paddingLeft: 12 },
  dupDismiss: { background: 'none', border: 'none', color: '#e53e3e', fontSize: 11, cursor: 'pointer', marginTop: 4, padding: 0, fontWeight: 600 },
  toggleBtn: { background: 'none', border: '1px solid #cbd5e0', color: '#0078d4', padding: '5px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  toggleBtnActive: { background: '#ebf5ff', border: '1px solid #0078d4', color: '#0078d4', padding: '5px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer', fontWeight: 600 },
};

/** Check if two receipts are likely duplicates */
function isDuplicate(a: ReceiptMetadata, b: ReceiptMetadata): boolean {
  if (a.receipt_id === b.receipt_id) return false;
  if (a.file_name === b.file_name && a.file_size_bytes === b.file_size_bytes && a.file_size_bytes > 0) return true;
  if (a.vendor_name && b.vendor_name && a.receipt_date && b.receipt_date && a.receipt_amount != null && b.receipt_amount != null) {
    if (a.vendor_name.toLowerCase() === b.vendor_name.toLowerCase() &&
        a.receipt_date === b.receipt_date &&
        Math.abs(a.receipt_amount - b.receipt_amount) < 0.01) {
      return true;
    }
  }
  return false;
}

export default function SelectReceiptsStep({ startDate, endDate, receipts, preSelectedIds, onReceiptsChange, onNext, onBack, nextLabel, backLabel }: Props) {
  const getApiToken = useApiToken();
  const [uploading, setUploading] = useState(false);
  const [availableReceipts, setAvailableReceipts] = useState<ReceiptMetadata[]>([]);
  const [outOfRangeReceipts, setOutOfRangeReceipts] = useState<ReceiptMetadata[]>([]);
  const [showOutOfRange, setShowOutOfRange] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(preSelectedIds || []));
  const [loading, setLoading] = useState(true);
  const [previewReceipt, setPreviewReceipt] = useState<ReceiptMetadata | null>(null);
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set());
  const [dupWarnings, setDupWarnings] = useState<string[]>([]);
  const [allReceipts, setAllReceipts] = useState<ReceiptMetadata[]>([]);
  const loadedRef = useRef(false);

  // Load receipts for the date range
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const token = await getApiToken();
        const [inRange, all] = await Promise.all([
          listReceiptsByDateRange(token, startDate, endDate),
          listReceipts(token),
        ]);
        setAllReceipts(all);

        const manualIds = new Set(receipts.filter(r => r.source === 'manual').map(r => r.receipt_id));
        const seen = new Set<string>();

        // In-range uncategorized receipts
        const available = inRange.filter((r) => {
          if (seen.has(r.receipt_id)) return false;
          seen.add(r.receipt_id);
          return r.status !== 'assigned' && !manualIds.has(r.receipt_id);
        });
        setAvailableReceipts(available);

        // Out-of-range uncategorized receipts (for the toggle)
        const inRangeIds = new Set(inRange.map(r => r.receipt_id));
        const outOfRange = all.filter(r =>
          !inRangeIds.has(r.receipt_id) && r.status !== 'assigned' && !manualIds.has(r.receipt_id)
        );
        setOutOfRangeReceipts(outOfRange);

        // Auto-select: all uncategorized in range + dashboard pre-selections + back navigation
        const uncatIds = available.filter(r => r.status === 'uncategorized').map(r => r.receipt_id);
        const previouslySelected = receipts.filter(r => r.source !== 'manual').map(r => r.receipt_id);
        const combined = new Set([...uncatIds, ...(preSelectedIds || []), ...previouslySelected]);
        if (combined.size > 0) {
          setSelectedIds(combined);
        }
      } catch (e) {
        console.error('Failed to load receipts:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [getApiToken, startDate, endDate, preSelectedIds]);

  const handleFileUpload = useCallback(async (files: FileList) => {
    setUploading(true);
    setDupWarnings([]);
    try {
      const token = await getApiToken();
      const kept: ReceiptMetadata[] = [];
      const dupsFound: string[] = [];
      // Check against ALL receipts (not just visible ones)
      const allExisting = [...receipts, ...availableReceipts, ...allReceipts];

      for (const file of Array.from(files)) {
        const receipt = await uploadReceipt(token, file);

        const matchedExisting = allExisting.find(r => isDuplicate(receipt, r));
        const matchedBatch = kept.find(r => isDuplicate(receipt, r));

        if (matchedExisting || matchedBatch) {
          const matchName = matchedExisting?.vendor_name || matchedExisting?.file_name || matchedBatch?.vendor_name || matchedBatch?.file_name || 'unknown';
          dupsFound.push(`"${receipt.vendor_name || receipt.file_name}" is a duplicate of "${matchName}" \u2014 removed`);
          try { await deleteReceipt(token, receipt.receipt_id); } catch {}
        } else {
          kept.push(receipt);
        }
      }

      if (kept.length > 0) {
        onReceiptsChange([...receipts, ...kept]);
      }
      if (dupsFound.length > 0) {
        setDupWarnings(dupsFound);
      }
    } catch (e) {
      alert(`Upload failed: ${e}`);
    } finally {
      setUploading(false);
    }
  }, [getApiToken, receipts, availableReceipts, allReceipts, onReceiptsChange]);

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const selectAll = () => {
    const allIds = availableReceipts.map(r => r.receipt_id);
    if (showOutOfRange) allIds.push(...outOfRangeReceipts.map(r => r.receipt_id));
    if (selectedIds.size === allIds.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  const handleDelete = async (receiptId: string) => {
    try {
      const token = await getApiToken();
      await deleteReceipt(token, receiptId);
      onReceiptsChange(receipts.filter((r) => r.receipt_id !== receiptId));
    } catch (e) {
      alert(`Delete failed: ${e}`);
    }
  };

  const handleReupload = (receiptId: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.jpg,.jpeg,.png,.pdf';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const token = await getApiToken();
        const updated = await replaceReceiptFile(token, receiptId, file);
        // Update whichever list holds this receipt
        setAvailableReceipts(prev => prev.map(r => r.receipt_id === receiptId ? updated : r));
        setOutOfRangeReceipts(prev => prev.map(r => r.receipt_id === receiptId ? updated : r));
        onReceiptsChange(receipts.map(r => r.receipt_id === receiptId ? updated : r));
      } catch (e) {
        alert(`Re-upload failed: ${e}`);
      }
    };
    input.click();
  };

  const handleNext = () => {
    const selectedReceipts = [...availableReceipts, ...outOfRangeReceipts].filter((r) => selectedIds.has(r.receipt_id));
    const manualReceipts = receipts.filter((r) => r.source === 'manual');
    const allIds = new Set(manualReceipts.map((r) => r.receipt_id));
    const merged = [...manualReceipts];
    for (const r of selectedReceipts) {
      if (!allIds.has(r.receipt_id)) {
        merged.push(r);
        allIds.add(r.receipt_id);
      }
    }
    onReceiptsChange(merged);
    onNext(merged);
  };

  const totalSelected = selectedIds.size + receipts.filter((r) => r.source === 'manual').length;

  const renderCard = (r: ReceiptMetadata, selectable: boolean, deletable: boolean = false) => {
    const isSelected = selectedIds.has(r.receipt_id);
    const fn = r.file_name.toLowerCase();
    const isPdf = fn.endsWith('.pdf') || fn.endsWith('.html');
    const category = deriveCategory(r);
    const cat = getCategoryStyle(category);
    const hasImgError = imgErrors.has(r.receipt_id);
    const cardStyle = isSelected
      ? { ...styles.receiptCardSelected, background: cat.cardBg, borderColor: '#0078d4' }
      : { ...styles.receiptCard, background: cat.cardBg, borderColor: cat.border };
    return (
      <div key={r.receipt_id} style={cardStyle} onClick={() => selectable && toggleSelect(r.receipt_id)}>
        {/* Category label at top */}
        <div style={{ background: cat.bg, padding: '3px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: cat.text }}>{cat.label}</span>
          {deletable && (
            <button
              style={styles.deleteBtn}
              onClick={(e) => { e.stopPropagation(); handleDelete(r.receipt_id); }}
              title="Delete receipt"
            >&times;</button>
          )}
        </div>
        {r.file_missing && (
          <div style={{ background: '#fff5f5', borderBottom: '1px solid #fc8181', padding: '4px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#9b2c2c' }}>&#9888; File missing</span>
            <button
              style={{ background: '#e53e3e', color: 'white', border: 'none', padding: '2px 8px', borderRadius: 3, fontSize: 10, cursor: 'pointer', fontWeight: 600 }}
              onClick={(e) => { e.stopPropagation(); handleReupload(r.receipt_id); }}
              title="Re-upload the file — keeps vendor/date/amount metadata"
            >Re-upload</button>
          </div>
        )}
        {selectable && (
          <div style={isSelected ? styles.checkboxSelected : styles.checkbox}>
            {isSelected && '\u2713'}
          </div>
        )}
        <div style={styles.previewBtn} onClick={(e) => { e.stopPropagation(); setPreviewReceipt(r); }} title="Preview">View</div>
        {isPdf || hasImgError ? (
          <div style={{...styles.imageFallback, background: cat.bg}}>
            <span style={{ fontSize: 36 }}>{cat.icon}</span>
          </div>
        ) : (
          <img src={r.download_url} alt={r.vendor_name || cat.label} style={styles.previewImg}
            onError={() => setImgErrors(prev => new Set(prev).add(r.receipt_id))} />
        )}
        <div style={styles.cardInfo}>
          <div style={styles.cardDate}>
            {r.is_directions ? (r.vendor_name || 'Directions') : (r.vendor_name || 'Unknown vendor')}
          </div>
          <div style={styles.cardDetail}>
            {r.receipt_date || 'Date pending'}
            {r.receipt_time && ` at ${r.receipt_time}`}
          </div>
          {r.receipt_amount != null && <div style={styles.cardAmount}>${r.receipt_amount.toFixed(2)}</div>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div
        style={styles.dropZone}
        onClick={() => document.getElementById('file-input')?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files.length) handleFileUpload(e.dataTransfer.files); }}
      >
        {uploading ? 'Uploading...' : 'Drop files here or click to select (JPEG, PNG, PDF)'}
      </div>
      <input id="file-input" type="file" multiple accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }}
        onChange={(e) => e.target.files && handleFileUpload(e.target.files)} />

      {dupWarnings.length > 0 && (
        <div style={styles.dupWarning}>
          <div style={styles.dupTitle}>&#9888; Duplicate receipts removed</div>
          {dupWarnings.map((w, i) => <div key={i} style={styles.dupItem}>&bull; {w}</div>)}
          <button style={styles.dupDismiss} onClick={() => setDupWarnings([])}>Dismiss</button>
        </div>
      )}

      {/* Manually uploaded receipts */}
      {receipts.filter((r) => r.source === 'manual').length > 0 && (
        <div style={styles.selectedReceiptsSection}>
          <h3 style={styles.sectionTitle}>Uploaded Receipts ({receipts.filter((r) => r.source === 'manual').length})</h3>
          <div style={styles.receiptGrid}>
            {receipts.filter((r) => r.source === 'manual').map((r) => renderCard(r, false, true))}
          </div>
        </div>
      )}

      {/* Available receipts for date range */}
      <h3 style={styles.sectionTitle}>
        Available Receipts ({startDate} to {endDate})
      </h3>
      <p style={styles.hint}>
        Uncategorized receipts in the date range are selected by default. Toggle below to include receipts outside the range (e.g. airfare, parking).
      </p>

      {loading ? (
        <LoadingAnimation />
      ) : availableReceipts.length === 0 && !showOutOfRange ? (
        <p style={styles.emptyState}>No receipts found for this date range. Upload files above or forward receipts to receipts@cultivategeospatial.com.</p>
      ) : (
        <>
          <div style={styles.toolbar}>
            <button style={styles.selectAllBtn} onClick={selectAll}>
              {selectedIds.size === (availableReceipts.length + (showOutOfRange ? outOfRangeReceipts.length : 0)) ? 'Deselect All' : 'Select All'}
            </button>
            <span style={{ fontSize: 12, color: '#666' }}>{selectedIds.size} selected</span>
            {outOfRangeReceipts.length > 0 && (
              <button
                style={showOutOfRange ? styles.toggleBtnActive : styles.toggleBtn}
                onClick={() => setShowOutOfRange(!showOutOfRange)}
              >
                {showOutOfRange ? `Hide ${outOfRangeReceipts.length} out-of-range` : `Show ${outOfRangeReceipts.length} out-of-range`}
              </button>
            )}
          </div>
          <div style={styles.receiptGrid}>
            {availableReceipts.map((r) => renderCard(r, true))}
          </div>

          {showOutOfRange && outOfRangeReceipts.length > 0 && (
            <>
              <h3 style={{ ...styles.sectionTitle, color: '#718096' }}>
                Out of Date Range ({outOfRangeReceipts.length})
              </h3>
              <div style={styles.receiptGrid}>
                {outOfRangeReceipts.map((r) => renderCard(r, true))}
              </div>
            </>
          )}
        </>
      )}

      <div style={styles.actions}>
        <button style={styles.btnOutline} onClick={onBack}>{backLabel || 'Back'}</button>
        <button style={styles.btn} onClick={handleNext}>
          {nextLabel || `Next: Review Line Items (${totalSelected} receipt${totalSelected !== 1 ? 's' : ''})`}
        </button>
      </div>

      <ReceiptPreviewModal
        receipt={previewReceipt}
        onClose={() => setPreviewReceipt(null)}
        onUpdate={(updated) => {
          setAvailableReceipts(prev => prev.map(r => r.receipt_id === updated.receipt_id ? updated : r));
          setOutOfRangeReceipts(prev => prev.map(r => r.receipt_id === updated.receipt_id ? updated : r));
          setPreviewReceipt(updated);
          onReceiptsChange(receipts.map(r => r.receipt_id === updated.receipt_id ? updated : r));
        }}
      />
    </div>
  );
}
